import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  updateProfile,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  serverTimestamp,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";

import { Capacitor } from "@capacitor/core";
import i18n from "@/i18n";

// ✅ Capgo Social Login
import { SocialLogin } from "@capgo/capacitor-social-login";

const firebaseConfig = {
  apiKey: "AIzaSyDVfDEcuL85SFHNxkneLgHIp-ggFn8JEAU",
  authDomain: "kalendario-4ec09.firebaseapp.com",
  projectId: "kalendario-4ec09",
  storageBucket: "kalendario-4ec09.firebasestorage.app",
  messagingSenderId: "987933643212",
  appId: "1:987933643212:web:a5bba6a87b3650c0464d69",
  measurementId: "G-W5H3GSKGCH",
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

export const googleProvider = new GoogleAuthProvider();

const LEGAL_VERSION = "2026-05-15";
const COOKIE_CONSENT_VERSION = "1.0";
const PUBLIC_APP_URL = process.env.VUE_APP_PUBLIC_URL || "https://kalendario.thevalkiria.com";

function buildLegalAcceptance() {
  return {
    acceptedAt: serverTimestamp(),
    legalNoticeVersion: LEGAL_VERSION,
    privacyPolicyVersion: LEGAL_VERSION,
    acceptedLegalNotice: true,
    acknowledgedPrivacyPolicy: true,
  };
}

function hasCurrentLegalAcceptance(legal) {
  return (
    legal?.acceptedLegalNotice === true &&
    (legal?.acknowledgedPrivacyPolicy === true || legal?.acceptedPrivacyPolicy === true) &&
    legal?.legalNoticeVersion === LEGAL_VERSION &&
    legal?.privacyPolicyVersion === LEGAL_VERSION
  );
}


export async function initSocialLogin() {
  if (!Capacitor.isNativePlatform()) return;

  await SocialLogin.initialize({
    google: {
      webClientId:
        "987933643212-m6vepb5ldn8qo7388mm1jj1i32ca9kcp.apps.googleusercontent.com",
    },
  });
}
export async function signInWithGoogle({ acceptedLegal = false } = {}) {
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    // 1) Login nativo (Capgo)
    const res = await SocialLogin.login({
      provider: "google",
      options: {}, // normalmente no hace falta nada aquí
    });

    // 2) Firebase credential con el ID token
    // Nota: el nombre exacto del campo puede ser idToken / token según versión.
    // En docs suelen devolver tokens en el objeto de resultado.
    const idToken =
      res?.result?.idToken || res?.result?.token || res?.idToken || res?.token;

    if (!idToken) throw new Error(i18n.t("errors.missingGoogleIdToken"));

    const cred = GoogleAuthProvider.credential(idToken);
    const fb = await signInWithCredential(auth, cred);

    await upsertUser(fb.user, { acceptedLegal });
    return fb.user;
  }

  // Web (popup)
  const web = await signInWithPopup(auth, googleProvider);
  await upsertUser(web.user, { acceptedLegal });
  return web.user;
}

export async function registerWithEmail({ name, email, password, acceptedLegal = false }) {
  const credentials = await createUserWithEmailAndPassword(
    auth,
    email.trim(),
    password
  );

  if (name?.trim()) {
    await updateProfile(credentials.user, {
      displayName: name.trim(),
    });
  }

  await upsertUser(auth.currentUser || credentials.user, { acceptedLegal });
  await sendVerificationEmail(auth.currentUser || credentials.user);
  await signOut(auth);
  return auth.currentUser || credentials.user;
}

export async function signInWithEmail({ email, password }) {
  const credentials = await signInWithEmailAndPassword(
    auth,
    email.trim(),
    password
  );

  await credentials.user.reload();

  if (!credentials.user.emailVerified) {
    await sendVerificationEmail(credentials.user);
    await signOut(auth);
    const error = new Error(i18n.t("login.emailVerificationRequired"));
    error.code = "auth/email-not-verified";
    throw error;
  }

  await upsertUser(credentials.user);
  return credentials.user;
}

function buildEmailVerificationSettings() {
  return {
    url: `${PUBLIC_APP_URL}/#/login?emailVerified=1`,
    handleCodeInApp: false,
  };
}

function buildPasswordResetSettings() {
  return {
    url: `${PUBLIC_APP_URL}/#/login?passwordReset=1`,
    handleCodeInApp: false,
  };
}

export async function sendVerificationEmail(user = auth.currentUser) {
  if (!user || user.emailVerified) return;
  await sendEmailVerification(user, buildEmailVerificationSettings());
}

export async function sendPasswordResetEmail(email) {
  await firebaseSendPasswordResetEmail(
    auth,
    email.trim(),
    buildPasswordResetSettings()
  );
}

export function needsEmailVerification(user) {
  return Boolean(
    user &&
      !user.emailVerified &&
      user.providerData?.some(provider => provider.providerId === "password")
  );
}

async function upsertUser(user, options = {}) {
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() || {} : {};
  const hasEventInviteNotifSetting =
    typeof existing?.settings?.notifications?.eventInviteRequests ===
    "boolean";

  if (!snap.exists()) {
    await setDoc(ref, {
      uid: user.uid,
      displayName: user.displayName || "",
      email: user.email || "",
      photoURL: user.photoURL || "",
      settings: {
        notifications: {
          eventInviteRequests: true,
        },
      },
      createdAt: serverTimestamp(),
      ...(options.acceptedLegal ? { legal: buildLegalAcceptance() } : {}),
    });
  } else {
    const payload = {
      displayName: user.displayName || "",
      email: user.email || "",
      photoURL: user.photoURL || "",
    };

    if (!hasEventInviteNotifSetting) {
      payload.settings = {
        notifications: {
          eventInviteRequests: true,
        },
      };
    }

    if (options.acceptedLegal && !hasCurrentLegalAcceptance(existing.legal)) {
      payload.legal = buildLegalAcceptance();
    }

    await setDoc(
      ref,
      payload,
      { merge: true }
    );
  }
}

export async function saveCookieConsent(uid, consent) {
  if (!uid || !consent) return;

  await setDoc(
    doc(db, "users", uid),
    {
      cookieConsent: {
        necessary: true,
        preferences: Boolean(consent.preferences),
        analytics: Boolean(consent.analytics),
        version: consent.version || COOKIE_CONSENT_VERSION,
        updatedAt: serverTimestamp(),
      },
    },
    { merge: true }
  );
}



export async function logout() {
  await signOut(auth);
  if (Capacitor.isNativePlatform()) {
    try {
      await SocialLogin.logout({ provider: "google" });
    } catch (_) {}
  }
}

export function listenAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

export { serverTimestamp };
